import { Close } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  approveFacilityReport,
  getFacilitiyByIdV2,
  getFacilityReport,
  publishFacilityReport,
  rejectFacilityReport,
} from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import RichtextEditor from '../../../common/RichtextEditor';

export default function ApproveResult({
  open,
  title,
  onClose,
  facilityId,
  reportQuarter,
  reportYear,
}) {
  const [report, setReport] = useState();
  const [reportLoading, setReportLoading] = useState(false);
  const [facility, setFacility] = useState();
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [notificationReceipientList, setNotificationReceipientList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState();
  const [remark, setRemark] = useState();
  const [sendInvoice, setSendInvoice] = useState(true);

  // console.log('sendInvoice', sendInvoice);
  // console.log('report', report);
  // console.log('facility', facility);
  // console.log('notificationReceipientList', notificationReceipientList);

  useEffect(() => {
    fetchReport();
    fetchFacility();
  }, []);

  const handleGetReceipients = (consultants, personnels) => {
    const rawList = [...(consultants || []), ...(personnels || [])];

    const reciepient = rawList.map((item) => ({
      facilityId: item?.facilityId,
      fullName: item?.fullName,
      emailAddress: item?.email,
      notify: true,
    }));

    setNotificationReceipientList(reciepient);
  };

  const handleRemoveReciepient = (itemToUpdate) => {
    const updatedList = notificationReceipientList.map((item) => {
      if (item.emailAddress === itemToUpdate.emailAddress) {
        return { ...item, notify: false };
      }
      return item;
    });

    setNotificationReceipientList(updatedList);
  };

  const handleApprove = async () => {
    setSaving(true);

    try {
      const recepientUser = notificationReceipientList?.filter((x) => x?.notify);

      const payload = {
        reportID: report?.reportId,
        emailMessage: '',
        recipients: !sendInvoice ? [] : recepientUser,
      };
      const res = await approveFacilityReport(payload);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errMsg = error?.data?.result?.status?.desc;
      openNotification({
        type: 'error',
        message: !errMsg ? 'Something went wrong. Please try again' : errMsg,
      });
    } finally {
      setSaving(false);
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const res = await getFacilityReport(facilityId, reportQuarter, reportYear);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setReport(data);
      }
    } catch (error) {
    } finally {
      setReportLoading(false);
    }
  };

  const fetchFacility = async () => {
    setFacilityLoading(true);
    try {
      const res = await getFacilitiyByIdV2(facilityId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setFacility(data);
        handleGetReceipients(data?.facilityConsultant, data?.facilityPersonel);
      }
    } catch (error) {
    } finally {
      setFacilityLoading(false);
    }
  };

  const handleRenderContent = () => {
    if (reportLoading) {
      return <LoaderSkeleton />;
    }

    if (!reportLoading && !report) {
      return null;
    }

    if (!reportLoading && report) {
      return renderContent();
    }
  };

  const renderContent = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Unified Alert Section */}
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 600 }}>Approve & Publish</AlertTitle>
          <Typography variant="body2" gutterBottom>
            Publishing <strong>{report?.reportType}</strong> results for{' '}
            <strong>{report?.facility}</strong>.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9, color: 'warning.dark' }}>
            <em>Note: If this action is completed, results can no longer be edited.</em>
          </Typography>
        </Alert>

        {/* Invoice Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendInvoice}
                  onChange={(e) => setSendInvoice(e.target.checked)}
                  color="primary"
                  slotProps={{
                    input: { 'aria-label': 'controlled' },
                  }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  Send Invoice via Email
                </Typography>
              }
            />
          </FormGroup>
        </Box>

        {/* Dynamic Recipients Section */}
        {sendInvoice && <Box>{renderReciepients()}</Box>}

        {/* Remark Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
            Add a remark for the Submitter
          </Typography>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'text.disabled' },
            }}
          >
            <RichtextEditor value={remark} onChange={(content) => setRemark(content)} />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderReciepients = () => {
    if (facilityLoading) {
      return <LoaderSkeleton />;
    }

    if (!facilityLoading && !facility) {
      return null;
    }

    if (!facilityLoading && facility) {
      return (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
            bgcolor: '#f8fbff', // A very subtle blue hint for the invoice section
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Email Chips Header */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Invoice Recipients
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              The following email addresses will receive the payment invoice:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {notificationReceipientList
                ?.filter((x) => x.notify)
                ?.map((i) => (
                  <Chip
                    key={i.emailAddress} // Added key to prevent console warnings
                    label={i?.emailAddress}
                    onDelete={() => handleRemoveReciepient(i)}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ bgcolor: 'background.paper' }}
                  />
                ))}
              {notificationReceipientList?.filter((x) => x.notify).length === 0 && (
                <Typography variant="caption" color="error">
                  No recipients selected.
                </Typography>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Invoice Message Editor */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Custom Email Message (Optional)
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: 'text.disabled' },
              }}
            >
              <RichtextEditor value={message} onChange={(content) => setMessage(content)} />
            </Box>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 } 
      }}
    >
      {/* Dialog Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        <DialogTitle variant="h6" fontWeight={700} sx={{ p: 0 }}>
          {title || 'Approve Result'}
        </DialogTitle>
        <IconButton 
          onClick={onClose} 
          disabled={saving}
          sx={{ color: 'text.secondary' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Dialog Content */}
      <DialogContent sx={{ px: 3, py: 2 }}>
        {handleRenderContent()}
      </DialogContent>

      {/* Dialog Footer */}
      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5, 
          borderTop: 1, 
          borderColor: 'divider',
          gap: 1 
        }}
      >
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          disabled={saving}
          onClick={onClose}
          sx={{ 
            textTransform: 'none', 
            borderRadius: 2,
            px: 3,
            borderColor: 'divider' 
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disableElevation
          disabled={saving}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
          sx={{ 
            textTransform: 'none', 
            borderRadius: 2,
            px: 4
          }}
          onClick={handleApprove}
        >
          {saving ? 'Approving...' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}